import {
  REPORT_FORMAT_VERSION,
  KEY_CANCEL,
  TYPE_EVENT_OTHER,
  DEVICE_EVENT_TAB_ENUM,
} from '@/shared/config';
import TTLCache from '@isaacs/ttlcache';
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { ANNOTATION_TYPE } from '@/shared/ui';
import {
  convertToMoment,
  convertToMomentWithUTC0,
  dateToStringWithDailyTimeZone,
  isVersionAtLeast,
} from '@/shared/lib';
import { convertEventNameFromType } from '@/shared/lib/aiUtils';
import { isCheckContinueData } from '@/entities/event';
import {
  addSineWaveToEcgData,
  convertBinaryToECGData,
  downloadSelectedEcgData,
} from '@/entities/ecg';
import { filterHourlySummaryRanges } from '@/entities/heart-rate';
import { getDataRange } from '@/shared/lib/timeRange';

export const ID_DIV_STRIP_MANAGEMENT = 'div-strip-management';
export const ID_DIV_DEVICE_STRIP_MANAGEMENT = 'div-device-strip-management';
export const SAVED_SRIPS_IMAGES: any[] = [];

/**
 * Decides whether a requested ECG disclosure window can be opened for the study. It resolves the recorded data range covering the start and stop moments and checks that range is continuous across the requested window.
 *
 * @param ecgDataMap - Study ECG summary map holding the recorded data ranges.
 * @param start - Requested window start.
 * @param stop - Requested window stop.
 * @returns True when continuous ECG data covers the requested window.
 */
export const isAllowSetEcgDisclosure = (ecgDataMap: any, { start, stop }: any) => {
  const range = getDataRange(ecgDataMap, moment(start), moment(stop));
  return isCheckContinueData(range, { start, stop });
};

/**
 * Builds the ECG download cache key for a time window. It joins the start and stop epoch values with a hyphen.
 *
 * @param start - Window start epoch.
 * @param stop - Window stop epoch.
 * @returns The cache key string for that start and stop pair.
 */
const generateId = (start: any, stop: any) => `${start}-${stop}`;
const CACHE_ECG = new TTLCache({ max: 800, ttl: 900000 }); // 15 minutes

/**
 * Downloads and assembles the ECG samples surrounding a beat or event so the viewer can draw a strip. It derives the window from an explicit moment pair, a configured custom offset or a symmetric offset, returns the cached entry when that start-stop key is already in CACHE_ECG, otherwise resolves the hourly summary ranges, downloads and decodes the binary channels, pads gaps with a sine wave and caches the result.
 *
 * @param ecgDataMap - Study ECG summary map with channels, sampling frequency and query signature.
 * @param beat - Beat or event whose start anchors the window.
 * @param offset - Symmetric millisecond offset around the anchor.
 * @param momentObject - Explicit start and stop moments overriding the offsets.
 * @param dataLength - Expected sample count used when padding the decoded data.
 * @param config - Options carrying customOffset.
 * @returns A promise resolving to the decoded ECG channel data for the window.
 */
export const handleDownloadData = async ({
  ecgDataMap,
  beat,
  offset,
  momentObject = {} as any,
  dataLength,
  config,
}: any) => {
  // *: dataLength = samplingRate * minutes
  const { customOffset } = config || {};
  const epoch = beat?.start ? new Date(beat?.start).getTime() : beat?.id;
  let startMoment: any;
  let stopMoment: any;
  if (!_.isEmpty(momentObject)) {
    // *: Draw selected event
    startMoment = momentObject.startMoment;
    stopMoment = momentObject.stopMoment;
  } else if (!_.isEmpty(customOffset)) {
    startMoment = moment(epoch - customOffset[0]);
    stopMoment = moment(epoch + customOffset[1]);
  } else if (offset) {
    startMoment = moment(epoch - offset);
    stopMoment = moment(epoch + offset);
  }
  const idAPI = generateId(startMoment.valueOf(), stopMoment.valueOf());
  if (CACHE_ECG.has(idAPI)) {
    return CACHE_ECG.get(idAPI);
  }
  const { channels, samplingFrequency } = ecgDataMap;
  const ranges: any = filterHourlySummaryRanges({
    summaries: ecgDataMap,
    start: startMoment,
    stop: stopMoment,
    channels,
    samplingFrequency,
    willAddDataRanges: true,
  } as any);
  try {
    const downloadedSelectedEcgData = await downloadSelectedEcgData(
      ranges,
      ecgDataMap.querySignature,
      { cancelKey: KEY_CANCEL.DOWLOAD_ECG },
    );
    if (downloadedSelectedEcgData?.length) {
      const selectedEcgData = convertBinaryToECGData(
        channels.length,
        downloadedSelectedEcgData as any,
      );
      const addedSineWaveSelectedEcgData = addSineWaveToEcgData(
        selectedEcgData,
        ranges,
        startMoment,
        stopMoment,
        ecgDataMap,
        dataLength,
      );
      CACHE_ECG.set(idAPI, addedSineWaveSelectedEcgData);
      return addedSineWaveSelectedEcgData;
    }
    throw new Error('Error');
  } catch (error) {
    console.log('Failed to download selected ecg data: ', error);
    throw error;
  }
};

/**
 * Flattens rhythm events and their strips into the row shape the strips table renders. Each strip becomes a row carrying the event metadata, drawn widgets, converted type text, event and trigger times formatted in the facility's daily timezone, and a frozen originalInfo snapshot used later for change detection, with rows ordered ascending by strip start epoch.
 *
 * @param events - Rhythm events with their nested strips.
 * @param timezone - Study timezone used to format displayed times.
 * @param facility - Facility whose daily timezone rules apply.
 * @param startStudyDate - Study start anchoring the daily timezone conversion.
 * @returns The flattened strip rows sorted by strip start time.
 */
export const formatStripByEvents = (
  events: any,
  timezone: any,
  facility: any,
  startStudyDate: any,
) => {
  const listStrips: any[] = [];
  _.forEach(events, (event, idx) => {
    const { strips } = event || {};
    _.forEach(strips, (strip, i) => {
      listStrips.push({
        id: strip.id,
        stripId: strip.id,
        stripStart: strip.start,
        stripStop: strip.stop,
        stripImgs: strip.images,
        draw: strip.draw || [],
        isSelected: strip.isSelected,
        eventStart: event.start,
        start: event.start,
        eventStop: event.stop,
        event: event.event,
        isIncludedToReport: !!event.isIncludedToReport,
        eventId: event.id,
        title: event.title,
        type: event.type,
        typeText: (convertEventNameFromType as any)[event.type],
        timezone,
        facility,
        startStudyDate,
        isOtherEvent: event.type === TYPE_EVENT_OTHER,
        isRemoved: false,
        isAutoCapture: !strip.userUpdated,
        eventTime: strip.start
          ? dateToStringWithDailyTimeZone({
              startStudyDate,
              isoDate: strip.start,
              timezone,
              facility,
              format: 'MM/DD, HH:mm:ss',
            })
          : event.start
            ? dateToStringWithDailyTimeZone({
                startStudyDate,
                isoDate: event.start,
                timezone,
                facility,
                format: 'MM/DD, HH:mm:ss',
              })
            : '',
        triggerTime: event.start
          ? dateToStringWithDailyTimeZone({
              startStudyDate,
              isoDate: event.start,
              timezone,
              facility,
            })
          : '',
        isIncludedStrip: !!event.isIncludedToReport && !!strip.isSelected,
        isShowValid: false,
        comment: strip.comment,
        updateStatus: '',
        startStripEpoch: moment(strip.start).valueOf(),
        originalInfo: {
          isIncludedToReport: !!event.isIncludedToReport,
          title: event.title,
          comment: strip.comment,
          isIncludedStrip: !!event.isIncludedToReport && !!strip.isSelected,
          stripImgs: strip.images,
          draw: strip.draw || [],
          isRemoved: false,
          updateStatus: '',
          stripStart: strip.start,
          stripStop: strip.stop,
        },
      });
    });
  });
  return _.orderBy(listStrips, ['startStripEpoch'], ['asc']);
};

/**
 * Normalizes the symptom text shown for a patient-triggered device event. The sentinel value None becomes Not specified, and any falsy value becomes an empty string.
 *
 * @param symptoms - Raw symptom text recorded on the event.
 * @returns The display text for the symptoms field.
 */
export const symptomsFormatter = (symptoms: any) =>
  symptoms === 'None' ? 'Not specified' : symptoms || '';
/**
 * Maps raw device events into the row shape used by the device tabs of the strips table. Each row carries event times formatted in the facility's daily timezone, the sample mark widgets pulled from hrInfo, a no-ECG-data flag derived from realtimeData, the carriedBySentReport flag read verbatim from the query, a report format version check marking newer report events, and an originalInfo snapshot for change detection.
 *
 * @param events - Raw device events for one bucket.
 * @param filterType - Device tab the rows belong to.
 * @param startStudyDate - Study start anchoring the daily timezone conversion.
 * @param timezone - Study timezone used to format displayed times.
 * @param facility - Facility whose daily timezone rules apply.
 * @returns The device event rows ready for the strips table.
 */
const formatDeviceEvent = (
  events: any,
  filterType: any,
  { startStudyDate, timezone, facility }: any,
) =>
  _.map(events, (event: any, index: any) => {
    const {
      id,
      friendlyId,
      type,
      start,
      stop,
      reason,
      comments,
      selected,
      stripImgs,
      isIncludedToReport,
      isValid,
      carriedBySentReport,
      hrInfo,
      realtimeData,
      reportData,
    } = event;
    const { report } = reportData || {};
    const isNoEcgData =
      _.isNil(realtimeData) ||
      _.isEmpty(realtimeData) ||
      (_.isNil(realtimeData?.start) && _.isNil(realtimeData?.stop));
    const isNewEventReport =
      isVersionAtLeast(report?.formatVersion, REPORT_FORMAT_VERSION.V202) || !report?.id;
    return {
      id,
      index,
      type,
      stop,
      start,
      symptoms: symptomsFormatter(reason),
      selected: !!selected,
      friendlyId,
      stripImgs,
      timezone,
      facility,
      stripStart: start,
      isShowSymptoms: type === 'Manual',
      isIncludedToReport: !!isIncludedToReport,
      startStudyDate,
      isValid: !!isValid,
      filterType,
      typeText: type,
      hrInfo,
      draw: hrInfo?.viewerObject?.widgets || [],
      isNoEcgData,
      eventTime: start
        ? dateToStringWithDailyTimeZone({
            startStudyDate,
            isoDate: start,
            timezone,
            facility,
            format: 'MM/DD, HH:mm:ss',
          })
        : '',
      triggerTime: start
        ? dateToStringWithDailyTimeZone({
            startStudyDate,
            isoDate: start,
            timezone,
            facility,
          })
        : '',
      isIncludedStrip: !!isIncludedToReport && !!selected,
      isShowValid: true,
      comment: comments?.[0] || '',
      isDeviceEvent: true,
      updateStatus: '',
      realtimeData,
      carriedBySentReport: !!carriedBySentReport,
      originalInfo: {
        comment: comments?.[0] || '',
        isIncludedToReport: !!isIncludedToReport,
        isIncludedStrip: !!isIncludedToReport && !!selected,
        isValid: !!isValid,
        stripImgs,
        updateStatus: '',
        isNoEcgData,
        carriedBySentReport: !!carriedBySentReport,
        hrInfo: hrInfo || {},
      },
      isNewEventReport,
    };
  });

/**
 * Formats exactly one device event bucket, notification, patient triggered or other, into strips table rows. It branches on the requested tab type and runs only that bucket's events through the row formatter with the shared timezone and facility config.
 *
 * @param deviceEvent - All three device event buckets as returned by the query.
 * @param timezone - Study timezone used to format displayed times.
 * @param facility - Facility whose daily timezone rules apply.
 * @param startStudyDate - Study start anchoring the daily timezone conversion.
 * @param type - Device tab whose bucket should be formatted.
 * @returns An object whose deviceEvents field holds the formatted rows keyed by tab type.
 */
export const formatDeviceEvents = (
  deviceEvent: any,
  timezone: any,
  facility: any,
  startStudyDate: any,
  type: any,
) => {
  const config = { timezone, facility, startStudyDate };
  const { notificationEvents, patientTriggeredEvents, otherEvents } = deviceEvent || {};
  const deviceEvents: Record<string, any> = {};
  if (type === DEVICE_EVENT_TAB_ENUM.NOTIFICATION) {
    _.assign(deviceEvents, {
      [DEVICE_EVENT_TAB_ENUM.NOTIFICATION]: formatDeviceEvent(
        notificationEvents.events,
        DEVICE_EVENT_TAB_ENUM.NOTIFICATION,
        config,
      ),
    });
  }
  if (type === DEVICE_EVENT_TAB_ENUM.PATIENT_TRIGGERED) {
    _.assign(deviceEvents, {
      [DEVICE_EVENT_TAB_ENUM.PATIENT_TRIGGERED]: formatDeviceEvent(
        patientTriggeredEvents.events,
        DEVICE_EVENT_TAB_ENUM.PATIENT_TRIGGERED,
        config,
      ),
    });
  }
  if (type === DEVICE_EVENT_TAB_ENUM.OTHER) {
    _.assign(deviceEvents, {
      [DEVICE_EVENT_TAB_ENUM.OTHER]: formatDeviceEvent(
        otherEvents.events,
        DEVICE_EVENT_TAB_ENUM.OTHER,
        config,
      ),
    });
  }
  return { deviceEvents };
};

/**
 * Reduces pending strip edits down to only those that actually differ from their captured original values, tagging each with an Edited or Removed status. It merges the new updates with the current changes by id, then compares comment, isIncludedToReport, isIncludedStrip and strip images against originalInfo, adding isValid for device events and title for the rest, keeping only entries that changed or were removed.
 *
 * @param newUpdates - Strips just edited by the user.
 * @param currentChanges - Strips already recorded in the pending change set.
 * @returns The changed strips, each carrying an updateStatus of Edited or Removed.
 */
export const checkStatusStripChange = (newUpdates: any[], currentChanges: any[]) => {
  const changeData = _.uniqBy([...newUpdates, ...currentChanges], 'id');
  const checkedUpdateList: any[] = [];
  _.forEach(changeData, (item) => {
    const updateData = _.cloneDeep(item);
    const {
      originalInfo,
      isDeviceEvent,
      comment,
      isIncludedToReport,
      title,
      isIncludedStrip,
      stripImgs,
      isValid,
      isRemoved,
    } = updateData || {};
    const {
      comment: originalComment,
      isIncludedToReport: originalIsIncludedToReport,
      isIncludedStrip: originalIsIncludedStrip,
      isValid: originalIsValid,
      stripImgs: originalStripImgs,
      title: originalTitle,
    } = originalInfo || {};
    if (updateData.updateStatus) {
      updateData.updateStatus = '';
    }
    if (isDeviceEvent) {
      if (
        originalComment !== comment ||
        originalIsIncludedToReport !== isIncludedToReport ||
        originalIsIncludedStrip !== isIncludedStrip ||
        originalIsValid !== isValid ||
        isRemoved ||
        !_.isEqual(originalStripImgs, stripImgs)
      ) {
        updateData.updateStatus = isRemoved ? 'Removed' : 'Edited';
        checkedUpdateList.push(updateData);
      }
    } else if (
      originalComment !== comment ||
      originalIsIncludedToReport !== isIncludedToReport ||
      originalTitle !== title ||
      originalIsIncludedStrip !== isIncludedStrip ||
      !_.isEqual(originalStripImgs, stripImgs) ||
      isRemoved
    ) {
      updateData.updateStatus = isRemoved ? 'Removed' : 'Edited';
      checkedUpdateList.push(updateData);
    }
  });
  return checkedUpdateList;
};

/**
 * Folds a flat list of edited strips into the per-event mutation payload the report API expects. Each removed strip becomes a delete action, otherwise images, comment and isIncludedStrip are diffed against originalInfo so only moved fields are sent alongside the sample mark start, stop and channel; strips are grouped under their eventId, and removed other-type events are marked deleted.
 *
 * @param stripEvents - Edited strips carrying eventId, draw widgets and originalInfo.
 * @returns The per-event update payloads, each with its list of strip actions.
 */
export const formatAiEventChangeByStrips = (stripEvents: any[]) => {
  const events: any[] = [];
  _.forEach(stripEvents, (x) => {
    const findEvent: any = _.find(events, (event: any) => event.id === x.eventId);
    const { originalInfo } = x || {};
    const strip: any = {};
    if (x.isRemoved) {
      _.assign(strip, {
        action: x.isRemoved ? 'delete' : 'update',
        id: x.id,
        eventId: x.eventId,
      });
    }
    if (!x.isRemoved) {
      const isUpdateImage = !_.isEqual(x.stripImgs, x.originalInfo.stripImgs);
      const findSampleDraw = _.find(x.draw, (y) => y.type === ANNOTATION_TYPE.SAMPLE_MARK);
      _.assign(strip, {
        images: isUpdateImage ? x.stripImgs : undefined,
        start: isUpdateImage ? findSampleDraw.startTime : undefined,
        stop: isUpdateImage ? findSampleDraw.endTime : undefined,
        draw: isUpdateImage ? x.draw : undefined,
        channel: isUpdateImage ? findSampleDraw.channelIndex + 1 : undefined,

        comment: x.comment === originalInfo.comment ? undefined : x.comment,
        isSelected: !x.isIncludedToReport
          ? false
          : x.isIncludedStrip === originalInfo.isIncludedStrip
            ? undefined
            : x.isIncludedStrip,
      });
      // check if strip is update some data
      if (isUpdateImage || strip.comment || x.isIncludedStrip !== originalInfo.isIncludedStrip) {
        _.assign(strip, {
          action: 'update',
          id: x.id,
          eventId: x.eventId,
        });
      }
    }
    if (findEvent) {
      const { strips } = findEvent || {};
      if (!_.isEmpty(strip)) {
        strips.push(strip);
      }
    } else {
      const isDeletedEvent = x.isRemoved && x.type === TYPE_EVENT_OTHER;
      events.push({
        isDeletedEvent,
        isIncludedToReport:
          x.isRemoved && x.type === TYPE_EVENT_OTHER ? false : x.isIncludedToReport,
        title: x.title && x.title === originalInfo.title ? undefined : x.title,
        strips: !_.isEmpty(strip) ? [strip] : undefined,
        isDeleted: x.isRemoved && x.type === TYPE_EVENT_OTHER ? true : undefined,
        id: x.eventId,
      });
    }
  });
  return events;
};

/**
 * Translates the device event filter form into the API filter payload for a study. It compares the chosen search dates against the study's own start and stop in its timezone and sends startFrom and startTo only when they were actually moved, adds type, isValid and a numeric eventFid when set, and reports whether anything differs from the default.
 *
 * @param stripFilter - Form values holding search dates, event type, validity and event fid.
 * @param study - Study whose start, stop and timezone define the default range.
 * @returns An object carrying hasAppliedFilter and the API filter payload.
 */
export const getDeviceFilter = ({ stripFilter, study }: any) => {
  const { startSearchDate, stopSearchDate, eventType, isValid, eventFid } = stripFilter;
  const filter: any = {};
  const { start, stop, timezone } = study || {};
  const defaultStartDate = convertToMoment(start, timezone);
  const defaultEndDate = convertToMoment(stop, timezone);

  const hasChangedStartSearchDate = startSearchDate
    ? moment(startSearchDate).format('MM/DD/YYYY HH:mm') !==
      moment(defaultStartDate).format('MM/DD/YYYY HH:mm')
    : false;
  const hasChangedStopSearchDate = stopSearchDate
    ? moment(stopSearchDate).format('MM/DD/YYYY HH:mm') !==
      moment(defaultEndDate).format('MM/DD/YYYY HH:mm')
    : false;
  if (hasChangedStopSearchDate || hasChangedStartSearchDate) {
    filter.startFrom = `${convertToMomentWithUTC0(`${startSearchDate.format('YYYY-MM-DDTHH:mm:ss')}.000Z`, timezone).format('YYYY-MM-DDTHH:mm:ss')}.000Z`;
    filter.startTo = `${convertToMomentWithUTC0(`${stopSearchDate.format('YYYY-MM-DDTHH:mm:ss')}.000Z`, timezone).format('YYYY-MM-DDTHH:mm:ss')}.000Z`;
  }
  if (!_.isNil(eventType)) {
    filter.type = eventType;
  }
  if (!_.isNil(isValid)) {
    filter.isValid = isValid;
  }
  if (eventFid) {
    filter.eventFid = parseFloat(eventFid);
  }
  const hasAppliedFilter =
    hasChangedStartSearchDate ||
    hasChangedStopSearchDate ||
    !_.isNil(eventType) ||
    !_.isNil(isValid) ||
    eventFid;

  return { hasAppliedFilter, filter };
};

/**
 * Fetches the SVG markup for a set of strip image URLs in parallel. It issues one axios GET per URL with an svg response type, awaits them together, and pairs each response body with the URL it came from.
 *
 * @param imageUrls - Remote strip image URLs to fetch.
 * @returns A promise resolving to one entry per URL holding its objectUrl markup and baseUrl.
 */
export const downloadImage = async (imageUrls: any[]) => {
  const downloadImagesPromises: any[] = [];
  _.forEach(imageUrls, (url) => {
    downloadImagesPromises.push(
      axios.get(url, {
        responseType: 'image/svg+xml' as any, // pre-existing: not a valid axios ResponseType
      }),
    );
  });
  const downloadedImages = await Promise.all(downloadImagesPromises);
  return _.map(downloadedImages, (image, index) => ({
    objectUrl: image.data,
    baseUrl: imageUrls[index],
  }));
};

/**
 * Warms the in-memory strip image cache for a set of events so their thumbnails render without a network round trip. It flattens every stripImgs entry off the given strips, bails out when there is nothing to fetch, downloads them all in parallel and pushes the results into the shared SAVED_SRIPS_IMAGES array.
 *
 * @param strips - Events or strips whose stripImgs should be pre-fetched.
 * @returns A promise resolving once the downloaded images have been pushed into the shared cache.
 */
export const downloadImageAndCache = async (strips: any[]) => {
  if (_.isEmpty(strips)) return;
  const stripImgs: any[] = [];
  _.forEach(strips, (strip) => {
    const { stripImgs: imgs } = strip || {};
    _.forEach(imgs, (img) => {
      stripImgs.push(img);
    });
  });
  if (_.isEmpty(stripImgs)) return;
  const imgs = await downloadImage(stripImgs);
  SAVED_SRIPS_IMAGES.push(...imgs);
};

/**
 * Pre-fetches strip images for every device event bucket returned by the study query. It concatenates the notification, patient-triggered and other event lists and hands the combined array to the image download and cache routine without awaiting it.
 *
 * @param deviceEvents - Bucketed device events carrying notificationEvents, patientTriggeredEvents and otherEvents.
 * @returns Nothing.
 */
export const cacheDeviceEvents = (deviceEvents: any) => {
  const { notificationEvents, patientTriggeredEvents, otherEvents } = deviceEvents;
  const events = [
    ...(notificationEvents || []),
    ...(patientTriggeredEvents || []),
    ...(otherEvents || []),
  ];
  downloadImageAndCache(events);
};

/**
 * Validates that a device event's capture sample mark falls entirely inside the realtime ECG data window available for that strip. It reads realtimeData and hrInfo off the strip, finds the SAMPLE_MARK widget among the viewer widgets, and compares its startTime and endTime against the realtime start and stop, returning false from a catch on any missing shape.
 *
 * @param strip - Device event strip carrying realtimeData and the hrInfo viewer widgets.
 * @returns True when the sample mark lies within the available realtime ECG range.
 */
export const checkIsValidDeviceStrip = (strip: any) => {
  try {
    const { realtimeData, hrInfo } = strip || {};
    const widgets = hrInfo?.viewerObject?.widgets || [];
    const sampleMarkStrip = _.find(widgets, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
    const { endTime, startTime } = sampleMarkStrip || {};
    const { start, stop } = realtimeData || {};
    return startTime && endTime && start && stop && startTime >= start && endTime <= stop;
  } catch {
    return false;
  }
};

/**
 * Computes the patch applied to a strip when one of its report inclusion fields is toggled. It sets the named field to the given value, and additionally forces isIncludedStrip off when isIncludedToReport is being cleared or when the strip is being included while its owning event is not.
 *
 * @param name - Field being changed.
 * @param value - New value for that field.
 * @param isIncludedToReport - Whether the owning event is currently included in the report.
 * @returns The patch holding the changed field and any forced isIncludedStrip correction.
 */
export const getUpdateValue = (name: any, value: any, isIncludedToReport: any) => {
  const updateData: any = {
    [name]: value,
  };
  if (name === 'isIncludedToReport' && !value) {
    updateData.isIncludedStrip = false;
  }
  if (name === 'isIncludedStrip' && value && !isIncludedToReport) {
    updateData.isIncludedStrip = false;
  }
  return updateData;
};
